import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Snackbar,
  CircularProgress,
  Card,
  CardContent,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { api } from '../services/api';
import { formatVendorCurrency } from '../utils/currency';
import { useAuth } from '../contexts/AuthContext';
import { canSeeVendorBillingAmounts } from '../utils/firmAccess';

interface PendingEstimate {
  id: number;
  case: {
    id: number;
    title: string;
    subtitle: string;
    project_id: number;
    project: {
      id: number;
      name: string;
      firm_id: number;
      firm: {
        id: number;
        name: string;
      };
    };
  };
  // Backend returns these as snake_case relationships
  vendor_user: {
    id: number;
    name: string;
    email: string;
    currency?: string | null;
  };
  vendor_firm: {
    id: number;
    name: string;
  };
  // Also support camelCase versions in case backend changes
  vendorUser?: {
    id: number;
    name: string;
    email: string;
    currency?: string | null;
  };
  vendorFirm?: {
    id: number;
    name: string;
  };
  billing_type: string;
  rate: number;
  quantity: number;
  total_amount: number;
  description: string;
  status: string;
  created_at: string;
}

const PendingEstimates: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const canSeeAmounts = canSeeVendorBillingAmounts(user);
  const [estimates, setEstimates] = useState<PendingEstimate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [filters, setFilters] = useState({
    vendor_firm: '',
    billing_type: '',
  });

  useEffect(() => {
    loadEstimates();
  }, []);

  const loadEstimates = async () => {
    try {
      setLoading(true);
      const response = await api.get('/estimates/pending');
      if (response.data.success) {
        // Debug logging can be enabled here if needed to troubleshoot vendor fields
        // if (response.data.data.length > 0) {
        //   console.log('First pending estimate object keys:', Object.keys(response.data.data[0]));
        // }
        setEstimates(response.data.data);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load estimates');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (estimateId: number) => {
    try {
      const response = await api.put(`/estimates/${estimateId}`, {
        action: 'approve'
      });
      
      if (response.data.success) {
        setSuccess('Estimate approved successfully');
        loadEstimates();
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to approve estimate');
    }
  };

  const handleReject = async (estimateId: number, reason: string) => {
    try {
      const response = await api.put(`/estimates/${estimateId}`, {
        action: 'reject',
        rejection_reason: reason
      });
      
      if (response.data.success) {
        setSuccess('Estimate rejected successfully');
        loadEstimates();
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to reject estimate');
    }
  };

  const getBillingTypeLabel = (type: string) => {
    switch (type) {
      case 'page_based': return 'Page Based';
      case 'case_based': return 'Case Based';
      case 'time_based': return 'Time Based';
      default: return type;
    }
  };

  const getQuantityLabel = (type: string) => {
    switch (type) {
      case 'page_based': return 'Pages';
      case 'case_based': return 'Cases';
      case 'time_based': return 'Hours';
      default: return 'Quantity';
    }
  };

  const getVendorUserName = (estimate: PendingEstimate): string => {
    return estimate.vendor_user?.name || estimate.vendorUser?.name || 'Unknown User';
  };

  const getVendorFirmName = (estimate: PendingEstimate): string => {
    return estimate.vendor_firm?.name || estimate.vendorFirm?.name || 'Unknown Firm';
  };

  const filteredEstimates = estimates.filter(estimate => {
    if (filters.vendor_firm && getVendorFirmName(estimate) !== filters.vendor_firm) return false;
    if (filters.billing_type && estimate.billing_type !== filters.billing_type) return false;
    return true;
  });

  const vendorFirms = Array.from(new Set(estimates.map(e => getVendorFirmName(e))));
  const billingTypes = Array.from(new Set(estimates.map(e => e.billing_type)));

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Pending Estimates</Typography>
        <Button variant="outlined" onClick={() => navigate(-1)}>
          Back
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Summary Cards */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' }, gap: 2, mb: 3 }}>
        <Card>
          <CardContent>
            <Typography color="text.secondary" gutterBottom>
              Total Pending
            </Typography>
            <Typography variant="h4">
              {estimates.length}
            </Typography>
          </CardContent>
        </Card>
        {canSeeAmounts && (
        <Card>
          <CardContent>
            <Typography color="text.secondary" gutterBottom>
              Total Amount
            </Typography>
            <Typography variant="h4">
              {formatVendorCurrency(
                estimates.reduce((sum, e) => sum + (Number(e.total_amount) || 0), 0),
                estimates[0]?.vendor_user?.currency || estimates[0]?.vendorUser?.currency
              )}
            </Typography>
          </CardContent>
        </Card>
        )}
        <Card>
          <CardContent>
            <Typography color="text.secondary" gutterBottom>
              Vendor Firms
            </Typography>
            <Typography variant="h4">
              {vendorFirms.length}
            </Typography>
          </CardContent>
        </Card>
      </Box>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>Filters</Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
          <FormControl fullWidth>
            <InputLabel>Vendor Firm</InputLabel>
            <Select
              value={filters.vendor_firm}
              onChange={(e) => setFilters({ ...filters, vendor_firm: e.target.value })}
              label="Vendor Firm"
            >
              <MenuItem value="">All Vendors</MenuItem>
              {vendorFirms.map(firm => (
                <MenuItem key={firm} value={firm}>{firm}</MenuItem>
              ))}
            </Select>
          </FormControl>
          
          <FormControl fullWidth>
            <InputLabel>Billing Type</InputLabel>
            <Select
              value={filters.billing_type}
              onChange={(e) => setFilters({ ...filters, billing_type: e.target.value })}
              label="Billing Type"
            >
              <MenuItem value="">All Types</MenuItem>
              {billingTypes.map(type => (
                <MenuItem key={type} value={type}>{getBillingTypeLabel(type)}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Paper>

      {/* Estimates Table */}
      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Case</TableCell>
                <TableCell>Client Firm</TableCell>
                <TableCell>Vendor</TableCell>
                <TableCell>Billing Type</TableCell>
                <TableCell>Quantity</TableCell>
                {canSeeAmounts && <TableCell>Rate</TableCell>}
                {canSeeAmounts && <TableCell>Total Amount</TableCell>}
                <TableCell>Submitted</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredEstimates.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} align="center">
                    <Typography color="text.secondary" sx={{ py: 3 }}>
                      No pending estimates found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredEstimates.map((estimate) => (
                  <EstimateRow 
                    key={estimate.id} 
                    estimate={estimate} 
                    onApprove={handleApprove}
                    onReject={handleReject}
                    onViewCase={() => navigate(`/cases/${estimate.case.id}`)}
                    canSeeAmounts={canSeeAmounts}
                  />
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Snackbar
        open={!!success}
        autoHideDuration={6000}
        onClose={() => setSuccess(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setSuccess(null)} severity="success">
          {success}
        </Alert>
      </Snackbar>
    </Box>
  );
};

// Estimate Row Component
interface EstimateRowProps {
  estimate: PendingEstimate;
  onApprove: (id: number) => void;
  onReject: (id: number, reason: string) => void;
  onViewCase: () => void;
  canSeeAmounts: boolean;
}

const EstimateRow: React.FC<EstimateRowProps> = ({ 
  estimate, 
  onApprove, 
  onReject, 
  onViewCase,
  canSeeAmounts,
}) => {
  const [showRejectForm, setShowRejectForm] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');

  const handleReject = () => {
    if (rejectionReason.trim()) {
      onReject(estimate.id, rejectionReason);
      setShowRejectForm(false);
      setRejectionReason('');
    }
  };

  const getBillingTypeLabel = (type: string) => {
    switch (type) {
      case 'page_based': return 'Page Based';
      case 'case_based': return 'Case Based';
      case 'time_based': return 'Time Based';
      default: return type;
    }
  };

  const getQuantityLabel = (type: string) => {
    switch (type) {
      case 'page_based': return 'Pages';
      case 'case_based': return 'Cases';
      case 'time_based': return 'Hours';
      default: return 'Quantity';
    }
  };

  return (
    <TableRow>
      <TableCell>
        <Box>
          <Typography variant="body2" fontWeight="bold" sx={{ cursor: 'pointer' }} onClick={onViewCase}>
            {estimate.case?.title || 'Unknown Case'}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {estimate.case?.subtitle || ''}
          </Typography>
        </Box>
      </TableCell>
      <TableCell>{estimate.case?.project?.firm?.name || 'Unknown Firm'}</TableCell>
      <TableCell>
        <Box>
          <Typography variant="body2" fontWeight="bold">
            {estimate.vendor_user?.name || estimate.vendorUser?.name || 'Unknown User'}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {estimate.vendor_firm?.name || estimate.vendorFirm?.name || 'Unknown Firm'}
          </Typography>
        </Box>
      </TableCell>
      <TableCell>
        <Chip label={getBillingTypeLabel(estimate.billing_type)} size="small" />
      </TableCell>
      <TableCell>
        {estimate.quantity} {getQuantityLabel(estimate.billing_type)}
      </TableCell>
      {canSeeAmounts && (
      <TableCell>
        {formatVendorCurrency(
          estimate.rate,
          estimate.vendor_user?.currency || estimate.vendorUser?.currency
        )}
      </TableCell>
      )}
      {canSeeAmounts && (
      <TableCell>
        <Typography fontWeight="bold">
          {formatVendorCurrency(
            estimate.total_amount,
            estimate.vendor_user?.currency || estimate.vendorUser?.currency
          )}
        </Typography>
      </TableCell>
      )}
      <TableCell>
        {new Date(estimate.created_at).toLocaleDateString()}
      </TableCell>
      <TableCell>
        {showRejectForm ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <TextField
              size="small"
              label="Rejection Reason"
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              multiline
              rows={2}
            />
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                size="small"
                variant="contained"
                color="error"
                onClick={handleReject}
                disabled={!rejectionReason.trim()}
              >
                Confirm Reject
              </Button>
              <Button
                size="small"
                variant="outlined"
                onClick={() => {
                  setShowRejectForm(false);
                  setRejectionReason('');
                }}
              >
                Cancel
              </Button>
            </Box>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              size="small"
              variant="contained"
              color="success"
              onClick={() => onApprove(estimate.id)}
            >
              Approve
            </Button>
            <Button
              size="small"
              variant="contained"
              color="error"
              onClick={() => setShowRejectForm(true)}
            >
              Reject
            </Button>
          </Box>
        )}
      </TableCell>
    </TableRow>
  );
};

export default PendingEstimates;
